import React, { useState, useEffect } from 'react';
import { Modal, Spin } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import './CouponAndVoucherDetail.scss';
import dayjs from 'dayjs';
import { SLASH_FORMAT } from 'constants/consts';
import {
  getQRCode,
  formatBase64Image,
} from 'services/qrcodeService';
import { BackendLoyaltyItem } from 'types/dto/wallet';

interface CouponAndVoucherDetailProps {
  visible: boolean;
  onClose: () => void;
  coupon: BackendLoyaltyItem;
}

const CouponAndVoucherDetail: React.FC<CouponAndVoucherDetailProps> = ({
  visible,
  onClose,
  coupon,
}) => {
  const { t, i18n } = useTranslation('wallet');
  const { t: tCommon } = useTranslation('common');
  const isEnglish = i18n.language === 'en';
  const [qrCodeImage, setQrCodeImage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);

  // 当优惠券或可见性改变时，获取二维码
  useEffect(() => {
    const fetchQRCode = async () => {
      if (!visible || !coupon) {
        setQrCodeImage('');
        return;
      }

      setLoading(true);
      setHasError(false);

      try {
        // `token` arrives Base64-encoded from `wallet/getloyaltyitems`. The QR endpoint encodes
        const qrCodeData = await getQRCode(window.btoa(coupon.token));

        // 格式化图片数据
        const formattedImage = formatBase64Image(qrCodeData);
        setQrCodeImage(formattedImage);
      } catch (err) {
        setHasError(true);
        setQrCodeImage('');
      } finally {
        setLoading(false);
      }
    };

    fetchQRCode();
  }, [visible, coupon, t]);

  // 重置状态当关闭弹窗时
  useEffect(() => {
    if (!visible) {
      setQrCodeImage('');
      setHasError(false);
      setLoading(false);
    }
  }, [visible]);

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      centered
      width={600}
      closeIcon={<CloseOutlined />}
      className="coupon-detail-modal"
    >
      <div className="coupon-detail-content">
        {hasError && (
          <div className="error-message">{tCommon('error.loadDataError')}</div>
        )}

        {/* QR Code Section */}
        <div className="coupon-detail-qr-section">
          {loading ? (
            <div className="qr-code-loading">
              <Spin size="large" />
            </div>
          ) : qrCodeImage ? (
            <img
              src={qrCodeImage}
              className="qr-code-image"
              onError={() => setHasError(true)}
            />
          ) : (
            <div className="qr-code-placeholder">
              {t('qrCodeNotAvailable')}
            </div>
          )}
        </div>

        {/* Title Section */}
        <div>
          <div className="coupon-detail-title">
            {isEnglish ? coupon.title.en : coupon.title.zh}
          </div>

          {/* Effective Date Section */}
          <div className="coupon-detail-seciton-detail">
            {t('effectiveDate')}
            {coupon.activate_at &&
              coupon.expire_at &&
              `${dayjs(coupon.activate_at).format(
                SLASH_FORMAT
              )} - ${dayjs(coupon.expire_at).format(SLASH_FORMAT)}`}
          </div>
        </div>

        <div>
          <div className="coupon-detail-seciton-title">{t('description')}</div>
          <div
            className="coupon-detail-seciton-detail"
            dangerouslySetInnerHTML={{__html: isEnglish ? coupon.details.en : coupon.details.zh}}
          />
        </div>

        {/* Terms and Conditions Section */}
        <div>
          <div className="coupon-detail-seciton-title">
            {t('termsAndConditions')}
          </div>
          <div
            className="coupon-detail-seciton-detail"
            dangerouslySetInnerHTML={{__html: isEnglish ? coupon.tnc.en : coupon.tnc.zh}}
          />
        </div>
      </div>
    </Modal>
  );
};

export default CouponAndVoucherDetail;
